import { ref, Ref } from 'vue'
import dataQueryManager from '../../managers/network/DataQueryManager'

export interface IMarca {
  id_user: number
  record_time: string
  machine_number: number
  record_type: number
  verify_mode: number
  workcode: number
  created_date: string
  created_by: number
  modified_date: string
  modified_by: number
  record_time2: string
  notificar: number
  hash: string | null
  id_marca: number
  ubicacion: string | null
  reclamo: string | null
}

export default class MarcasServices {
  constructor() {}

  startup() {}

  public async getMarcasByUser(userId: number): Promise<IMarca[]> {
    try {
      const body = {
        op_select_marca_usuario: {
          id_user: userId,
        },
      }
      const response = await dataQueryManager.addQuery<IMarca | IMarca[]>({
        query: `${import.meta.env.VITE_BASE_URL}/dashboard/1/op_select_marca_usuario`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data
      } else if (response.data) {
        if (response.data) {
          return [response.data]
        }
      }
    } catch (error) {
      console.error('Error fetching marcas by user:', error)
    }
    return []
  }

  public async getMarcasByDispositivo(machineNumber: number): Promise<IMarca[]> {
    try {
      const body = {
        op_select_marca_dispostivo: {
          machine_number: machineNumber,
        },
      }

      const response = await dataQueryManager.addQuery<IMarca | IMarca[]>({
        query: `${import.meta.env.VITE_BASE_URL}/dashboard/1/op_select_marca_dispostivo`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data
      } else if (response.data) {
        return [response.data]
      }
    } catch (error) {
      console.error('Error fetching marcas by dispositivo:', error)
    }
    return []
  }

  public async getMarcasByDateRange(startDate: string, endDate: string): Promise<IMarca[]> {
    try {
      const body = {
        op_select_marca_fecha: {
          record_time1: startDate,
          record_time2: endDate,
        },
      }
      const response = await dataQueryManager.addQuery<IMarca | IMarca[]>({
        query: `${import.meta.env.VITE_BASE_URL}/dashboard/1/op_select_marca_fecha`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data
      } else if (response.data) {
        return [response.data]
      }
    } catch (error) {
      console.error('Error fetching marcas by date range:', error)
    }
    return []
  }
}

// Se encarga de acumular y gestionar la información de marcas por fecha, permitiendo expandir el rango de fechas y recargar los datos.
export class MarcasController {
  private service = new MarcasServices()
  private fechaInicio: Date | undefined
  private fechaFin: Date | undefined
  private marcasData: IMarca[] = []
  public marcasList: Ref<IMarca[]> = ref([])

  private acumulateMode: boolean = false
  private acumulativeCallback: (() => void) | null = null

  private reloadTimeout: number | null = null
  private reloadDelay: number = 500 // Tiempo en ms para esperar antes de recargar datos al expandir fechas
  private activeReload: boolean = false

  constructor() {}

  public async loadData(fechaInicio: Date, fechaFin: Date, reloadDelay?: number) {
    let needReload = false

    // Fecha inicio y fecha fin siempre son los extremos del rango
    if (!this.fechaInicio || fechaInicio < this.fechaInicio) {
      this.fechaInicio = fechaInicio
      needReload = true
    }
    if (!this.fechaFin || fechaFin > this.fechaFin) {
      this.fechaFin = fechaFin
      needReload = true
    }

    const startDateStr = this.fechaInicio.toISOString()
    const endDateStr = this.fechaFin.toISOString()

    if (needReload && this.fechaFin > this.fechaInicio) {
      this.marcasData = await this.service.getMarcasByDateRange(startDateStr, endDateStr)
      this.marcasList.value = this.marcasData
    }

    if (reloadDelay !== undefined) {
      this.reloadDelay = reloadDelay
      this.activeReload = true
      if (this.reloadTimeout) {
        clearTimeout(this.reloadTimeout)
      }
      this.reloadTimeout = window.setTimeout(() => {
        this.reloadData()
      }, this.reloadDelay)
    }

    return this.marcasData.filter((marca) => {
      const marcaDate = new Date(marca.record_time)
      return marcaDate >= fechaInicio && marcaDate <= fechaFin
    })
  }

  public async reloadData() {
    if (!this.activeReload) return

    if (this.fechaInicio && this.fechaFin) {
      const startDateStr = this.fechaInicio.toISOString()
      const endDateStr = this.fechaFin.toISOString()

      this.marcasData = await this.service.getMarcasByDateRange(startDateStr, endDateStr)
      this.marcasList.value = this.marcasData

      if (this.acumulativeCallback) {
        this.acumulativeCallback()
      }

      this.reloadTimeout = window.setTimeout(() => {
        this.reloadData()
      }, this.reloadDelay)
    }
  }

  public activateAutoReload(delay: number) {
    this.reloadDelay = delay
    this.activeReload = true
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout)
    }
    this.reloadTimeout = window.setTimeout(() => {
      this.reloadData()
    }, this.reloadDelay)
  }

  public deactivateAutoReload() {
    this.activeReload = false
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout)
      this.reloadTimeout = null
    }
  }

  public startAcumulativeLoad(callback?: () => void) {
    this.fechaInicio = undefined
    this.fechaFin = undefined
    this.marcasData = []
    this.marcasList.value = []
    this.acumulateMode = true
    this.acumulativeCallback = callback || null
  }

  public addDates(fechaInicio: Date, fechaFin: Date) {
    if (!this.acumulateMode) {
      throw new Error('Acumulative mode is not active. Call startAcumulativeLoad first.')
    }

    if (!this.fechaInicio || fechaInicio < this.fechaInicio) {
      this.fechaInicio = fechaInicio
    }
    if (!this.fechaFin || fechaFin > this.fechaFin) {
      this.fechaFin = fechaFin
    }
  }

  public async finalizeAcumulativeLoad() {
    if (!this.acumulateMode) {
      throw new Error('Acumulative mode is not active. Call startAcumulativeLoad first.')
    }

    const startDateStr = this.fechaInicio!.toISOString()
    const endDateStr = this.fechaFin!.toISOString()

    this.marcasData = await this.service.getMarcasByDateRange(startDateStr, endDateStr)
    this.marcasList.value = this.marcasData
    this.acumulateMode = false

    if (this.acumulativeCallback) {
      this.acumulativeCallback()
    }
  }

  public isAcumulativeModeActive(): boolean {
    return this.acumulateMode
  }

  public getMarcasData(): IMarca[] {
    return this.marcasData
  }
}
